import { useQuery } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import {
  Field,
  Label,
  Radio,
  RadioGroup,
  Select,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import React from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import { GET_IN_HOUSE_CASH_CENTER_TIERS } from '../../../utils/statApi/CompanyCreationApi';
import IDropdownReference from '../../../utils/types/IDropdownReference';
import IFieldsetProps from '../../../utils/types/IFieldsetProps';
import CurrencyCodeDropdown from '../../common/formFields/CurrencyCodeDropdown';
import QueryBasedDropdown from '../../common/formFields/queryBasedDropdown/QueryBasedDropdown';
import { RequiredMarker } from '../../common/formFields/RequiredMarker';
import RequiredFieldsNotification from '../RequiredNotification';
import { IInHouseCashCenterSetup, RequiredFieldsType } from './types';
import { yesOption, noOption } from '../../../utils/types/CommonDropdownOptions';
import IDropdownOption from '../../../utils/types/IDropdownOption';

interface IInHouseCashCenterSetupFormProps {
  form: UseFormMethods<IInHouseCashCenterSetup>;
  data: IInHouseCashCenterSetup;
}

const requiredLabelStyles = { paddingBottom: 0 };

const postingModeOptions: IDropdownOption[] = [
  { key: null, text: '--' },
  { key: 'SYSTEMATICALLY', text: 'Systematically' },
  { key: 'MANUALLY', text: 'Manually' },
];

const InHouseCashCenterSetupForm = ({
  form,
  data,
}: IInHouseCashCenterSetupFormProps): JSX.Element => {
  const { control, errors, watch, setValue } = form;

  const onIhccTierChange = (value: IDropdownOption): void => {
    setValue('inHouseCashCenterTier', value);
  };

  const IhccTierDropdown = (props: IFieldsetProps): JSX.Element => {
    const {
      value,
      label,
      required,
      disabled,
      errors: tierErrors,
      handleChange,
      valueKey,
      outputKey,
    } = props;
    const { loading, data: tierData } = useQuery(GET_IN_HOUSE_CASH_CENTER_TIERS);
    const { inHouseCashCenterTiers } = !loading && tierData ? tierData : [];
    return (
      <>
        <Label style={requiredLabelStyles}>
          {label} <RequiredMarker />
        </Label>
        <QueryBasedDropdown
          value={value as IDropdownReference}
          outputKey={outputKey}
          required={required}
          disabled={disabled}
          errors={tierErrors}
          dropdownOptionsData={inHouseCashCenterTiers}
          handleChange={handleChange}
          valueKey={valueKey}
        />
      </>
    );
  };

  const isIncludedInInHouseCashCenter = watch(
    'isIncludedInInHouseCashCenter',
    data?.isIncludedInInHouseCashCenter?.toString().toLowerCase() || null,
  );

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <RequiredFieldsNotification type={RequiredFieldsType.SapSetup} />
        <Controller
          name="isIncludedInInHouseCashCenter"
          control={control}
          render={() => {
            return (
              <Field label="Should this entity be included in IHCC?" required>
                <RadioGroup
                  id="isIncludedInInHouseCashCenter"
                  name="isIncludedInInHouseCashCenter"
                  value={isIncludedInInHouseCashCenter}
                  onChange={(e, radioData): void => {
                    setValue('isIncludedInInHouseCashCenter', radioData.value);
                    if (radioData.value === 'false') {
                      setValue('inHouseCashCenterTier', null);
                      setValue('inHouseCashCenterAccountCurrency', null);
                      setValue('inHouseCashCenterPostingMode', null);
                    }
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          name="inHouseCashCenterTier"
          valueKey="inHouseCashCenterTier"
          defaultValue={data?.inHouseCashCenterTier}
          control={control}
          render={(props) => {
            return (
              <IhccTierDropdown
                label="IHCC Tier"
                valueKey="inHouseCashCenterTier"
                errors={errors}
                disabled={isIncludedInInHouseCashCenter !== 'true'}
                handleChange={onIhccTierChange}
                // eslint-disable-next-line react/prop-types
                value={props.value}
              />
            );
          }}
        />
        <Controller
          name="inHouseCashCenterAccountCurrency"
          control={control}
          defaultValue={data?.inHouseCashCenterAccountCurrency}
          render={(): React.ReactElement => (
            <CurrencyCodeDropdown
              label={
                <Label style={requiredLabelStyles}>
                  Account Currency in IHCC Module <RequiredMarker />
                </Label>
              }
              propertyName="inHouseCashCenterAccountCurrency"
              handleChange={(newValue: string, valueKey: string): void => {
                setValue('inHouseCashCenterAccountCurrency', newValue);
              }}
              disabled={isIncludedInInHouseCashCenter !== 'true'}
              defaultValue={data?.inHouseCashCenterAccountCurrency}
              width={500}
            />
          )}
        />
        <Controller
          name="inHouseCashCenterPostingMode"
          control={control}
          defaultValue={data?.inHouseCashCenterPostingMode}
          render={({ value }): React.ReactElement => (
            <>
              <Label style={requiredLabelStyles}>
                Will IHCC transaction be posted systematically (via IHCC module) or manually (via
                JEM)?
                <RequiredMarker />
              </Label>
              <Select
                onChange={(event): void => {
                  const selectedOption = postingModeOptions.find(
                    (option) => option.key === event.target.value,
                  );
                  setValue('inHouseCashCenterPostingMode', selectedOption?.key);
                }}
                disabled={isIncludedInInHouseCashCenter !== 'true'}
                defaultValue={value}
              >
                {postingModeOptions.map((option) => (
                  <option value={option.key}>{option.text}</option>
                ))}
              </Select>
            </>
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default InHouseCashCenterSetupForm;
